/**
 * AddSiteNoteModal Component
 * Modal for adding a new note to a site (internal, handover, or client feedback)
 */

import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

const NOTE_TYPES = [
  {
    value: 'internal',
    label: 'Internal Note',
    description: 'Internal team notes (not visible to clients)',
    color: 'blue'
  },
  {
    value: 'handover',
    label: 'Handover Note',
    description: 'Notes for project handover to site team',
    color: 'purple'
  },
  {
    value: 'clientFeedback',
    label: 'Client Feedback',
    description: 'Feedback or comments from the client',
    color: 'green'
  }
];

const AddSiteNoteModal = ({
  isOpen,
  onClose,
  onConfirm,
  site,
  loading = false
}) => {
  const [noteType, setNoteType] = useState('internal');
  const [content, setContent] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNoteType('internal');
      setContent('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    onConfirm && onConfirm({ noteType, content: content.trim() });
  };

  const selectedType = NOTE_TYPES.find(t => t.value === noteType);

  // Get existing note for selected type
  const getExistingNote = () => {
    if (!site) return null;
    switch (noteType) {
      case 'internal':
        return site.internalNotes;
      case 'handover':
        return site.handoverNotes;
      case 'clientFeedback':
        return site.clientFeedback;
      default:
        return null;
    }
  };

  const existingNote = getExistingNote();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Site Note">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Note Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note Type
            </label>
            <div className="space-y-2">
              {NOTE_TYPES.map(type => (
                <label
                  key={type.value}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    noteType === type.value
                      ? `border-${type.color}-500 bg-${type.color}-50`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="noteType"
                    value={type.value}
                    checked={noteType === type.value}
                    onChange={(e) => setNoteType(e.target.value)}
                    className={`mt-0.5 text-${type.color}-600 focus:ring-${type.color}-500`}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{type.label}</p>
                    <p className="text-xs text-gray-500">{type.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Existing Note Warning */}
          {existingNote && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-800">Existing note will be replaced</p>
                  <p className="text-xs text-amber-600 mt-1">Current: "{existingNote.substring(0, 100)}{existingNote.length > 100 ? '...' : ''}"</p>
                </div>
              </div>
            </div>
          )}

          {/* Note Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Enter ${selectedType?.label.toLowerCase() || 'note'}...`}
              required
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !content.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : existingNote ? 'Update Note' : 'Add Note'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddSiteNoteModal;
